import {useMessages} from "i18n";
import type {LadderPresentation} from "./ladderOffer";
import {Body, Headline, Strip} from "./LadderOfferNotice.styles";

/**
 * What a reader who followed a ladder link is told, above the plan table
 * (kitecorp/transferify-ui#1977).
 *
 * A dumb view: it renders whichever of the two sentences the presentation names and decides nothing.
 * The live strip names the offer and carries NO figure (kitecorp/transferify-ui#2545): the rung prices
 * every monthly tier, and each plan card below states its own first invoice and renewal price, the
 * way the welcome band names the run and the cards carry the amounts. No percentage is stated; the
 * two-price presentation is the one the owner settled on ui#2441 and ui#2456, and whether "first month X, then Y" counts as a reduction announcement under OG 99/2000 art. 33 is the open counsel question recorded on ui#2456.
 * The `intro` presentation renders NOTHING here on purpose — that rung is not a discount off a list
 * price, so it has a surface of its own. `referralGift` renders nothing for the same reason and one more
 * (kitecorp/transferify-ui#2033): that rung carried the friend's GIFT, which the picker states in its
 * own words, and a strip beside it would be a second offer on one page.
 *
 * <b>`role="status"`, not an alert.</b> The strip appears without the reader having acted, so it must
 * announce itself; but nothing is wrong and nothing needs interrupting, which is what an alert would
 * claim.
 */
export function LadderOfferNotice({presentation}: {presentation: LadderPresentation}) {
	const {offerLadder} = useMessages();

	if (presentation.kind === "expired") {
		return (
			<Strip $tone="expired" role="status" aria-label={offerLadder.expired.regionAria}>
				<Headline>{offerLadder.expired.title}</Headline>
				<Body>{offerLadder.expired.body}</Body>
			</Strip>
		);
	}
	if (presentation.kind !== "discount") {
		return null;
	}
	return (
		<Strip $tone="offer" role="status" aria-label={offerLadder.live.regionAria}>
			<Headline>
				{offerLadder.live.title(presentation.months)}
			</Headline>
			<Body>{offerLadder.live.body}</Body>
		</Strip>
	);
}
